/**
 * 위험 이벤트 유형 → 표시명·bbox 색 설정맵 (브라우저 저장).
 * 이벤트/수동이벤트에서 직접 지정한 값이 맵보다 우선한다.
 */

export type RiskAppearanceEntry = {
  /** 표시용 라벨 (비우면 type 코드 사용) */
  label: string;
  /** bbox 선 색 (#rrggbb) */
  color: string;
};

export type RiskAppearanceMap = Record<string, RiskAppearanceEntry>;

const STORAGE_KEY = 'riskEventAppearanceMap_v1';

/** 내장 기본값 — 사용자가 맵에서 덮어쓸 수 있음 */
export const DEFAULT_RISK_APPEARANCE: RiskAppearanceMap = {
  drowning: { label: '익사', color: '#ff1744' },
  no_motion: { label: '움직임없음', color: '#ff1744' },
  sinking: { label: '가라앉음', color: '#ff1744' },
  drowning_risk: { label: '익사 위험', color: '#ff1744' },
  motion_risk: { label: '위험 동작', color: '#ff1744' },
  slip_fall: { label: '미끄러짐·전도', color: '#ff9100' },
  fallen_immobile: { label: '쓰러짐', color: '#ff6d00' },
  collision_person: { label: '보행 충돌', color: '#f50057' },
  loitering: { label: '배회', color: '#ffea00' },
  special_zone_dwell: { label: '특별관리구역 체류', color: '#ff1744' },
  intrusion: { label: '침입', color: '#d500f9' },
  crowd_density: { label: '밀집', color: '#ff3d00' },
  crowd_stampede: { label: '압사 징후', color: '#b71c1c' },
  near_miss: { label: '아차사고', color: '#ff6f00' },
  fall_height: { label: '고소 전도', color: '#e65100' },
  fire_smoke: { label: '화재·연기', color: '#ff1744' },
  ppe_violation: { label: 'PPE 위반', color: '#ffab00' },
  collision_vehicle: { label: '차-보행', color: '#c51162' },
  alert_message: { label: '알림 메시지', color: '#2979ff' },
};

export const DEFAULT_RISK_BBOX_COLOR = '#ff1744';

function normalizeHexColor(raw: string | undefined | null, fallback = DEFAULT_RISK_BBOX_COLOR): string {
  const s = String(raw || '').trim();
  if (!s) return fallback;
  const withHash = s.startsWith('#') ? s : `#${s}`;
  if (/^#[0-9a-fA-F]{6}$/.test(withHash)) return withHash.toLowerCase();
  if (/^#[0-9a-fA-F]{3}$/.test(withHash)) {
    const r = withHash[1];
    const g = withHash[2];
    const b = withHash[3];
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }
  return fallback;
}

export function hexToRgba(hex: string, alpha: number): string {
  const h = normalizeHexColor(hex);
  const n = parseInt(h.slice(1), 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return `rgba(${r},${g},${b},${alpha})`;
}

export function loadRiskAppearanceMap(): RiskAppearanceMap {
  const out: RiskAppearanceMap = { ...DEFAULT_RISK_APPEARANCE };
  if (typeof window === 'undefined') return out;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return out;
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return out;
    Object.entries(parsed as Record<string, unknown>).forEach(([key, val]) => {
      const k = String(key || '').trim().toLowerCase();
      if (!k || !val || typeof val !== 'object') return;
      const v = val as Partial<RiskAppearanceEntry>;
      out[k] = {
        label: String(v.label ?? out[k]?.label ?? k).trim() || k,
        color: normalizeHexColor(v.color, out[k]?.color || DEFAULT_RISK_BBOX_COLOR),
      };
    });
  } catch {
    /* ignore */
  }
  return out;
}

export function saveRiskAppearanceMap(map: RiskAppearanceMap): void {
  if (typeof window === 'undefined') return;
  try {
    const cleaned: RiskAppearanceMap = {};
    Object.entries(map || {}).forEach(([key, val]) => {
      const k = String(key || '').trim().toLowerCase();
      if (!k || !val) return;
      cleaned[k] = {
        label: String(val.label || k).trim() || k,
        color: normalizeHexColor(val.color),
      };
    });
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cleaned));
  } catch {
    /* ignore */
  }
}

export function resolveRiskAppearance(
  riskType: string | undefined | null,
  overrides?: { label?: string | null; color?: string | null }
): RiskAppearanceEntry {
  const map = loadRiskAppearanceMap();
  const t = String(riskType || '').trim().toLowerCase();
  const base = (t && map[t]) || {
    label: t || '위험',
    color: DEFAULT_RISK_BBOX_COLOR,
  };
  const labelOverride = String(overrides?.label || '').trim();
  const colorOverride = String(overrides?.color || '').trim();
  return {
    label: labelOverride || base.label || t || '위험',
    color: colorOverride
      ? normalizeHexColor(colorOverride, base.color)
      : normalizeHexColor(base.color),
  };
}

export function upsertRiskAppearanceEntry(
  riskType: string,
  entry: Partial<RiskAppearanceEntry>
): RiskAppearanceMap {
  const map = loadRiskAppearanceMap();
  const k = String(riskType || '').trim().toLowerCase();
  if (!k) return map;
  const prev = map[k] || { label: k, color: DEFAULT_RISK_BBOX_COLOR };
  map[k] = {
    label: String(entry.label ?? prev.label).trim() || k,
    color: normalizeHexColor(entry.color ?? prev.color),
  };
  saveRiskAppearanceMap(map);
  return map;
}
